import { Fragment, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { PageHead, Skel, Empty, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

type Severity = 'sev1' | 'sev2' | 'sev3'
type IncidentStatus = 'open' | 'mitigating' | 'resolved' | 'postmortem'
type SlaState = 'ok' | 'due_soon' | 'breached'

interface IncidentStep {
  id: number
  step_no: number
  title: string
  detail: string
  done: boolean
  done_at?: string
  done_by?: string
}

interface Incident {
  id: string
  severity: Severity
  title: string
  detected_at: string
  acknowledged_at?: string
  resolved_at?: string
  status: IncidentStatus
  owner_id?: string
  summary: string
  created_at: string
  updated_at: string
  ack_due_at: string
  resolve_due_at: string
  sla_state: SlaState
  steps_done: number
  steps_total: number
  steps?: IncidentStep[]
}

interface IncidentStats {
  open: number
  breached: number
  mtta_minutes: number
  mttr_hours: number
}

const severityLabel: Record<Severity, string> = {
  sev1: 'SEV1 · วิกฤต', sev2: 'SEV2 · สำคัญ', sev3: 'SEV3 · เฝ้าระวัง',
}
const severityTone: Record<Severity, string> = {
  sev1: 'sev-critical', sev2: 'sev-high', sev3: 'sev-medium',
}
const statusLabel: Record<IncidentStatus, string> = {
  open: 'เปิดเหตุ', mitigating: 'กำลังรับมือ', resolved: 'แก้ไขแล้ว', postmortem: 'สรุปแล้ว',
}
const filters: { value: IncidentStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'open', label: 'เปิดเหตุ' },
  { value: 'mitigating', label: 'กำลังรับมือ' },
  { value: 'resolved', label: 'แก้ไขแล้ว' },
  { value: 'postmortem', label: 'สรุปแล้ว' },
]

const nextSteps: Record<IncidentStatus, { to: IncidentStatus; label: string; primary?: boolean }[]> = {
  open: [
    { to: 'mitigating', label: 'เริ่มรับมือ', primary: true },
    { to: 'resolved', label: 'แก้ไขเสร็จ' },
  ],
  mitigating: [
    { to: 'resolved', label: 'แก้ไขเสร็จ', primary: true },
    { to: 'open', label: 'กลับไปเปิดเหตุ' },
  ],
  resolved: [
    { to: 'postmortem', label: 'บันทึกบทเรียน', primary: true },
    { to: 'mitigating', label: 'กลับไปรับมือ' },
  ],
  postmortem: [],
}

/** Hours and minutes until the next deadline, or since it passed. */
function remaining(iso: string, now: number): string {
  const ms = new Date(iso).getTime() - now
  const abs = Math.abs(ms)
  const days = Math.floor(abs / 86_400_000)
  const hours = Math.floor((abs % 86_400_000) / 3_600_000)
  const mins = Math.floor((abs % 3_600_000) / 60_000)
  const span = days > 0 ? `${days} วัน ${hours} ชม.` : hours > 0 ? `${hours} ชม. ${mins} น.` : `${mins} น.`
  return ms < 0 ? `เกิน ${span}` : `เหลือ ${span}`
}

function isClosed(status: IncidentStatus): boolean {
  return status === 'resolved' || status === 'postmortem'
}

function SlaCell({ inc, now }: { inc: Incident; now: number }) {
  if (isClosed(inc.status)) {
    return (
      <div>
        <span className="dim">หยุดเวลาแล้ว</span>
        {inc.resolved_at && (
          <div className="dim tnum" style={{ fontSize: 11, marginTop: 4 }}>แก้ไข {thaiDateTime(inc.resolved_at)}</div>
        )}
      </div>
    )
  }
  const ackPending = !inc.acknowledged_at
  const due = ackPending ? inc.ack_due_at : inc.resolve_due_at
  const tone = inc.sla_state === 'breached' ? 'sev-critical' : inc.sla_state === 'due_soon' ? 'sev-high' : 'sev-low'
  return (
    <div>
      <span className={`sev ${tone}`}>
        <i className="sev-dot" />
        {remaining(due, now)}
      </span>
      <div className="dim tnum" style={{ fontSize: 11, marginTop: 4 }}>
        {ackPending ? 'ยืนยันเหตุ' : 'แก้ไข'} ภายใน {thaiDateTime(due)}
      </div>
    </div>
  )
}

function StepProgress({ inc }: { inc: Incident }) {
  const pct = inc.steps_total > 0 ? Math.round((inc.steps_done / inc.steps_total) * 100) : 0
  return (
    <div className="row" style={{ gap: 8 }}>
      <span className="bar" style={{ width: 64 }}>
        <i style={{ width: `${pct}%`, background: inc.steps_done === inc.steps_total ? 'var(--color-emerald)' : undefined }} />
      </span>
      <span className="tnum dim" style={{ fontSize: 11 }}>{inc.steps_done}/{inc.steps_total}</span>
    </div>
  )
}

export function Incidents() {
  const qc = useQueryClient()
  const [filter, setFilter] = useState<IncidentStatus | 'all'>('all')
  const [selected, setSelected] = useState<string | null>(null)
  const [composing, setComposing] = useState(false)
  const [title, setTitle] = useState('')
  const [severity, setSeverity] = useState<Severity>('sev2')
  const [summary, setSummary] = useState('')
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(timer)
  }, [])

  const incidents = useQuery({
    queryKey: ['incidents', filter],
    queryFn: () => api<Incident[]>(`/v1/incidents${filter === 'all' ? '' : `?status=${filter}`}`),
    staleTime: 15_000,
  })
  const stats = useQuery({
    queryKey: ['incident-stats'],
    queryFn: () => api<IncidentStats>('/v1/incidents/stats'),
    staleTime: 15_000,
  })
  const detail = useQuery({
    queryKey: ['incident', selected],
    queryFn: () => api<Incident>(`/v1/incidents/${selected}`),
    enabled: selected !== null,
    staleTime: 5_000,
  })

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['incidents'] })
    void qc.invalidateQueries({ queryKey: ['incident-stats'] })
  }

  const ack = useMutation({
    mutationFn: ({ id, stepNo }: { id: string; stepNo: number }) =>
      apiWrite<Incident>(`/v1/incidents/${id}/steps/${stepNo}/ack`, 'POST'),
    onSuccess: (data) => {
      qc.setQueryData(['incident', data.id], data)
      refresh()
    },
  })
  const move = useMutation({
    mutationFn: ({ id, status }: { id: string; status: IncidentStatus }) =>
      apiWrite<Incident>(`/v1/incidents/${id}`, 'PATCH', { status }),
    onSuccess: (data) => {
      qc.setQueryData(['incident', data.id], data)
      refresh()
    },
  })
  const create = useMutation({
    mutationFn: () => apiWrite<Incident>('/v1/incidents', 'POST', {
      title, severity, summary,
    }),
    onSuccess: () => {
      setComposing(false)
      setTitle('')
      setSummary('')
      setSeverity('sev2')
      refresh()
    },
  })

  const rows = incidents.data ?? []
  const s = stats.data
  const d = detail.data

  return (
    <>
      <PageHead
        title="รับมือเหตุการณ์"
        sub="เพลย์บุ๊กหกขั้นเดียวกันทุกระดับ · เวลาวิ่งจากเวลาตรวจพบ และหยุดเมื่อยืนยันหรือแก้ไขเสร็จ"
        actions={
          <button className="btn btn-primary" onClick={() => setComposing((v) => !v)}>
            {composing ? 'ยกเลิก' : 'ประกาศเหตุใหม่'}
          </button>
        }
      />

      <div className="grid-4" style={{ marginBottom: 16 }}>
        <Stat label="เปิดอยู่" value={s ? s.open : '—'} unit="เรื่อง" foot="รวมที่กำลังรับมือ" />
        <Stat
          label="เกิน SLA"
          value={<span style={{ color: s && s.breached > 0 ? 'var(--color-crimson)' : undefined }}>{s ? s.breached : '—'}</span>}
          unit="เรื่อง"
          foot="ยังไม่ยืนยันหรือเลยกำหนดแก้ไข"
        />
        <Stat label="ยืนยันเฉลี่ย" value={s ? s.mtta_minutes.toFixed(1) : '—'} unit="นาที" foot="นับจากเวลาตรวจพบ" />
        <Stat label="แก้ไขเฉลี่ย" value={s ? s.mttr_hours.toFixed(1) : '—'} unit="ชม." foot="นับถึงเวลาปิดเหตุ" />
      </div>

      {composing && (
        <div className="card card-pad rise" style={{ marginBottom: 16 }}>
          <div className="grid-2">
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="incident-title">ชื่อเหตุการณ์</label>
              <input id="incident-title" className="input" style={{ width: '100%' }} maxLength={200}
                value={title} onChange={(e) => setTitle(e.target.value)} placeholder="เช่น ระบบยืนยันยอดล่มทั้งประเทศ" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="incident-severity">ระดับความรุนแรง</label>
              <select id="incident-severity" className="select" style={{ width: '100%' }}
                value={severity} onChange={(e) => setSeverity(e.target.value as Severity)}>
                <option value="sev1">SEV1 · วิกฤต · ยืนยัน 15 นาที / แก้ไข 4 ชม.</option>
                <option value="sev2">SEV2 · สำคัญ · ยืนยัน 1 ชม. / แก้ไข 24 ชม.</option>
                <option value="sev3">SEV3 · เฝ้าระวัง · ยืนยัน 4 ชม. / แก้ไข 72 ชม.</option>
              </select>
            </div>
          </div>
          <div className="field">
            <label htmlFor="incident-summary">สรุปเหตุ</label>
            <textarea id="incident-summary" className="input" rows={3} style={{ width: '100%', height: 'auto', padding: '10px 12px' }}
              maxLength={20000} value={summary} onChange={(e) => setSummary(e.target.value)}
              placeholder="สิ่งที่ตรวจพบ ผลกระทบ และสิ่งที่ลงมือไปแล้ว" />
          </div>
          <div className="row" style={{ marginTop: 14, gap: 10 }}>
            <button className="btn btn-primary" disabled={create.isPending || !title.trim()} onClick={() => create.mutate()}>
              เปิดเหตุและสร้างเช็กลิสต์
            </button>
            {create.error && <span className="sev sev-critical"><i className="sev-dot" />{(create.error as Error).message}</span>}
          </div>
        </div>
      )}

      <div className="filters">
        {filters.map((f) => (
          <button key={f.value} className={`chip${filter === f.value ? ' chip-on' : ''}`} onClick={() => setFilter(f.value)}>
            {f.label}
          </button>
        ))}
      </div>

      {incidents.isLoading ? <Skel h={240} /> : rows.length === 0 ? (
        <Empty title="ยังไม่มีเหตุการณ์ในมุมมองนี้" body="ประกาศเหตุเมื่อตรวจพบปัญหา ระบบจะเริ่มนับ SLA และสร้างเช็กลิสต์หกขั้นให้ทันที" />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>ระดับ</th><th>เหตุการณ์</th><th>สถานะ</th><th>เช็กลิสต์</th><th>SLA</th><th>ตรวจพบเมื่อ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((inc) => (
                <Fragment key={inc.id}>
                  <tr onClick={() => setSelected(selected === inc.id ? null : inc.id)}>
                    <td>
                      <span className={`sev ${severityTone[inc.severity]}`}>
                        <i className="sev-dot" />
                        {severityLabel[inc.severity]}
                      </span>
                    </td>
                    <td style={{ maxWidth: 360 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inc.title}</div>
                      {inc.acknowledged_at && (
                        <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>ยืนยันเหตุ {thaiDateTime(inc.acknowledged_at)}</div>
                      )}
                    </td>
                    <td><span className="chip">{statusLabel[inc.status]}</span></td>
                    <td><StepProgress inc={inc} /></td>
                    <td><SlaCell inc={inc} now={now} /></td>
                    <td className="dim">{thaiDateTime(inc.detected_at)}</td>
                  </tr>
                  {selected === inc.id && (
                    <tr>
                      <td colSpan={6} style={{ background: 'var(--color-wash)', padding: 0 }}>
                        <div style={{ padding: '18px 20px' }}>
                          {detail.isLoading ? <Skel h={180} /> : detail.error ? (
                            <span className="sev sev-critical"><i className="sev-dot" />{(detail.error as Error).message}</span>
                          ) : d && d.id === inc.id ? (
                            <>
                              <div className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
                                <div style={{ minWidth: 0 }}>
                                  <div className="row" style={{ gap: 10, marginBottom: 6 }}>
                                    <span className={`sev ${severityTone[d.severity]}`}><i className="sev-dot" />{severityLabel[d.severity]}</span>
                                    <span className="chip">{statusLabel[d.status]}</span>
                                  </div>
                                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, letterSpacing: '-0.01em' }}>{d.title}</div>
                                </div>
                                <button className="btn btn-sm btn-ghost" style={{ marginLeft: 'auto' }} onClick={() => setSelected(null)}>ปิด</button>
                              </div>

                              {d.summary && (
                                <p style={{ fontSize: 13, lineHeight: 1.75, whiteSpace: 'pre-wrap', marginTop: 12 }}>{d.summary}</p>
                              )}

                              <div className="grid-2" style={{ marginTop: 14, gap: 12 }}>
                                <div>
                                  <div className="card-label">กำหนดยืนยันเหตุ</div>
                                  <div className="tnum" style={{ fontSize: 13, marginTop: 6 }}>{thaiDateTime(d.ack_due_at)}</div>
                                  <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>
                                    {d.acknowledged_at ? `ยืนยันแล้ว ${thaiDateTime(d.acknowledged_at)}` : 'ยังไม่ยืนยันเหตุ'}
                                  </div>
                                </div>
                                <div>
                                  <div className="card-label">กำหนดแก้ไขเสร็จ</div>
                                  <div className="tnum" style={{ fontSize: 13, marginTop: 6 }}>{thaiDateTime(d.resolve_due_at)}</div>
                                  <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>
                                    {d.resolved_at ? `แก้ไขแล้ว ${thaiDateTime(d.resolved_at)}` : 'ยังไม่แก้ไขเสร็จ'}
                                  </div>
                                </div>
                              </div>

                              <div className="card-label" style={{ marginTop: 18 }}>เช็กลิสต์ตามเพลย์บุ๊ก</div>
                              <div style={{ marginTop: 6 }}>
                                {(d.steps ?? []).map((st) => (
                                  <div key={st.id} style={{
                                    display: 'grid', gridTemplateColumns: '26px minmax(0, 1fr) auto', gap: 12,
                                    alignItems: 'start', padding: '12px 0', borderTop: '1px solid var(--color-hairline)',
                                  }}>
                                    <div style={{
                                      width: 22, height: 22, borderRadius: 6, fontSize: 11,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      border: `1px solid ${st.done ? 'var(--color-emerald)' : 'var(--color-hairline)'}`,
                                      background: st.done ? 'var(--color-emerald)' : 'transparent',
                                      color: st.done ? 'var(--color-ink)' : 'var(--color-ivory-dim)',
                                    }}>{st.step_no}</div>
                                    <div>
                                      <div style={{
                                        fontSize: 13,
                                        color: st.done ? 'var(--color-ivory-dim)' : undefined,
                                        textDecoration: st.done ? 'line-through' : undefined,
                                      }}>{st.title}</div>
                                      <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.7, marginTop: 4 }}>{st.detail}</div>
                                    </div>
                                    {st.done ? (
                                      <span className="chip">เสร็จแล้ว{st.done_at ? ` · ${thaiDateTime(st.done_at)}` : ''}</span>
                                    ) : (
                                      <button className="btn btn-sm" disabled={ack.isPending}
                                        onClick={() => ack.mutate({ id: d.id, stepNo: st.step_no })}>
                                        ทำเครื่องหมายเสร็จ
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                              {ack.error && <span className="sev sev-critical"><i className="sev-dot" />{(ack.error as Error).message}</span>}

                              <div className="row" style={{ gap: 8, marginTop: 16 }}>
                                {nextSteps[d.status].map((step) => (
                                  <button key={step.to} className={`btn btn-sm${step.primary ? ' btn-primary' : ''}`}
                                    disabled={move.isPending}
                                    onClick={() => move.mutate({ id: d.id, status: step.to })}>
                                    {step.label}
                                  </button>
                                ))}
                                {nextSteps[d.status].length === 0 && <span className="dim" style={{ fontSize: 12 }}>เหตุนี้ปิดด้วยบทเรียนแล้ว</span>}
                                {move.error && <span className="sev sev-critical"><i className="sev-dot" />{(move.error as Error).message}</span>}
                              </div>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
